// Google Reviews Service
// This service fetches reviews from Google Places API

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
const PLACE_ID = process.env.REACT_APP_GOOGLE_PLACE_ID; // You'll need to get this for "Ali Cranta"

// Google Places API endpoints
const PLACE_DETAILS_URL = 'https://maps.googleapis.com/maps/api/place/details/json';

export const fetchGoogleReviews = async () => {
  try {
    if (!GOOGLE_PLACES_API_KEY || !PLACE_ID) {
      console.warn('Google Places API key or Place ID not configured');
      return { reviews: [], rating: 0, totalReviews: 0 };
    }

    // Using a proxy/backend to avoid CORS issues
    // You'll need to implement this endpoint in your backend
    const proxyUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    
    const response = await fetch(`${proxyUrl}/google-reviews`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch Google reviews');
    }

    const data = await response.json();
    
    // Process and sort reviews by rating
    const processedReviews = data.reviews
      .filter(review => review.rating >= 4) // Only show 4+ star reviews
      .sort((a, b) => b.rating - a.rating) // Sort by highest rating first
      .slice(0, 10) // Limit to 10 reviews max
      .map(review => ({
        id: `google-${review.time}`,
        name: review.author_name,
        text: review.text,
        rating: review.rating,
        time: review.relative_time_description,
        profilePhoto: review.profile_photo_url,
        source: 'google',
        verified: true,
      }));

    return {
      reviews: processedReviews,
      rating: data.rating || 0,
      totalReviews: data.user_ratings_total || 0,
      place: {
        name: data.name,
        address: data.formatted_address,
        phone: data.formatted_phone_number,
        website: data.website,
        photos: data.photos?.slice(0, 3) || [],
      }
    };

  } catch (error) {
    console.error('Error fetching Google reviews:', error);
    return { reviews: [], rating: 0, totalReviews: 0 };
  }
};

// Alternative method using direct API call (requires CORS proxy)
export const fetchGoogleReviewsDirect = async () => {
  try {
    if (!GOOGLE_PLACES_API_KEY || !PLACE_ID) {
      console.warn('Google Places API key or Place ID not configured');
      return { reviews: [], rating: 0, totalReviews: 0 };
    }

    // This requires a CORS proxy since Google API doesn't allow direct browser calls
    const corsProxy = 'https://cors-anywhere.herokuapp.com/';
    const url = `${corsProxy}${PLACE_DETAILS_URL}?place_id=${PLACE_ID}&fields=name,rating,reviews,user_ratings_total,formatted_address,formatted_phone_number,website,photos&key=${GOOGLE_PLACES_API_KEY}`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.status !== 'OK') {
      throw new Error(`Google Places API error: ${data.status}`);
    }

    const result = data.result;
    const processedReviews = (result.reviews || [])
      .filter(review => review.rating >= 4)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 10)
      .map(review => ({
        id: `google-${review.time}`,
        name: review.author_name,
        text: review.text,
        rating: review.rating,
        time: review.relative_time_description,
        profilePhoto: review.profile_photo_url,
        source: 'google',
        verified: true,
      }));

    return {
      reviews: processedReviews,
      rating: result.rating || 0,
      totalReviews: result.user_ratings_total || 0,
      place: {
        name: result.name,
        address: result.formatted_address,
        phone: result.formatted_phone_number,
        website: result.website,
        photos: result.photos?.slice(0, 3) || [],
      }
    };

  } catch (error) {
    console.error('Error fetching Google reviews directly:', error);
    return { reviews: [], rating: 0, totalReviews: 0 };
  }
};

// Mock data for development/testing
export const getMockGoogleReviews = () => {
  return {
    reviews: [
      {
        id: 'google-1',
        name: 'Maria Ionescu',
        text: 'Dr. Ali Cranta este un medic exceptional! M-a ajutat enorm cu problemele respiratorii. Clinica este modernă și personalul foarte prietenos.',
        rating: 5,
        time: 'acum 2 săptămâni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-2',
        name: 'Andrei Popescu',
        text: 'Servicii de înaltă calitate! Spirometria a fost făcută profesionist și rezultatele explicate în detaliu. Recomand cu încredere!',
        rating: 5,
        time: 'acum o lună',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-3',
        name: 'Elena Stoica',
        text: 'Consultația pentru apnee de somn a fost foarte detaliată. Doctorul a fost foarte atent și mi-a oferit soluții practice.',
        rating: 5,
        time: 'acum 2 luni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-4',
        name: 'Ion Gheorghe',
        text: 'Personal foarte competent și empatic. Am primit tratamentul potrivit pentru problemele mele pulmonare.',
        rating: 4,
        time: 'acum 3 luni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-5',
        name: 'Ana-Maria Vasile',
        text: 'Clinică modernă cu echipamente de ultimă generație. Procesul pentru fișa auto a fost rapid și eficient.',
        rating: 4,
        time: 'acum 4 luni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-6',
        name: 'Cristian Munteanu',
        text: 'Excelentă experiență! Dr. Ali Cranta și echipa sa sunt foarte profesioniști. Investigațiile pulmonare au fost făcute cu grijă și precizie.',
        rating: 5,
        time: 'acum o săptămână',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-7',
        name: 'Mihaela Dumitru',
        text: 'Recomand cu toată încrederea! Am primit un tratament personalizat pentru problemele respiratorii. Starea mea s-a îmbunătățit considerabil.',
        rating: 5,
        time: 'acum 3 săptămâni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-8',
        name: 'Gabriel Radu',
        text: 'Servicii medicale de top! Testarea pentru injecție s-a desfășurat perfect, iar personalul a fost foarte atent la nevoile mele.',
        rating: 4,
        time: 'acum 5 săptămâni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      },
      {
        id: 'google-9',
        name: 'Daniela Petrescu',
        text: 'Foarte mulțumită de consultația pentru certificatul medical! Procesul a fost transparent și doctorul foarte comunicativ. Clinică foarte curată și organizată.',
        rating: 5,
        time: 'acum 2 luni',
        profilePhoto: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        source: 'google',
        verified: true,
      }
    ],
    rating: 4.9,
    totalReviews: 156,
    place: {
      name: 'Ali Cranta Medical Center',
      address: 'Strada Doctor Leonte Anastasievici nr 11, Sector 5, București',
      phone: '+40 725 596 895',
      website: 'https://your-website.com',
      photos: []
    }
  };
}; 